/**
 * "Sort by" control shared by the Kanban board and the list view
 * (FRIDAY-75).
 *
 * A labelled `<select>` with the sort key, plus a direction toggle. The
 * control is deliberately small: it sits in a toolbar next to the view
 * switcher and the list filters.
 */

import { useId } from 'react'
import { cn } from '@/lib/utils'
import type { SortDirection } from '@/features/projects/sorting'

export interface SortOption {
  value: string
  label: string
}

export function SortSelect({
  value,
  options,
  direction,
  onChange,
  onDirectionChange,
  label = 'Sort by',
  className,
}: {
  value: string
  options: readonly SortOption[]
  direction: SortDirection
  onChange: (value: string) => void
  onDirectionChange: (direction: SortDirection) => void
  label?: string
  className?: string
}) {
  const id = useId()
  const nextDirection: SortDirection = direction === 'asc' ? 'desc' : 'asc'
  return (
    <div className={cn('flex items-center gap-1', className)}>
      <label htmlFor={id} className="text-surface-500 shrink-0">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1 text-xs"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onDirectionChange(nextDirection)}
        aria-label={direction === 'asc' ? 'Sort descending' : 'Sort ascending'}
        title={direction === 'asc' ? 'Ascending' : 'Descending'}
        className="btn-ghost px-1.5 py-1 text-xs"
      >
        {direction === 'asc' ? '▲' : '▼'}
      </button>
    </div>
  )
}
